<template>
  <div class="layer-participation-editor">
    <div v-if="participations.length === 0" class="text-muted p-2 border rounded bg-light mb-2">
      <i class="fas fa-info-circle mr-1"></i>No participations added. Click "Add Participation" to add one.
    </div>

    <div
      v-for="(participation, index) in participations"
      :key="index"
      class="border rounded p-3 mb-2 bg-white"
    >
      <div class="row align-items-center">
        <!-- Policy Picker -->
        <div class="col-lg-4 col-md-6 col-12">
          <b-form-group class="mb-0">
            <label class="control-label">Policy</label>
            <b-form-select
              :value="participation.policyId"
              @change="updateParticipation(index, 'policyId', $event)"
            >
              <template #first>
                <b-form-select-option :value="null">Select a Policy</b-form-select-option>
              </template>
              <b-form-select-option
                v-for="policy in policies"
                :key="policy.id"
                :value="policy.id"
              >
                {{ policy.policyNumber || policy.carrierName || policy.id }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </div>

        <!-- Carrier Name label (read from policy) -->
        <div class="col-lg-2 col-md-6 col-12">
          <b-form-group class="mb-0">
            <label class="control-label">Carrier</label>
            <p class="form-control-plaintext mb-0">{{ getCarrierName(participation.policyId) }}</p>
          </b-form-group>
        </div>

        <!-- Participation % -->
        <div class="col-lg-2 col-md-4 col-12">
          <b-form-group class="mb-0">
            <label class="control-label">% Share</label>
            <div class="input-group">
              <b-form-input
                type="number"
                :value="toPercent(participation.participationPercent)"
                @input="onPercentInput(index, $event)"
                min="0"
                max="100"
                step="0.01"
                placeholder="0"
              ></b-form-input>
              <div class="input-group-append">
                <span class="input-group-text">%</span>
              </div>
            </div>
          </b-form-group>
        </div>

        <!-- Role Toggle -->
        <div class="col-lg-2 col-md-4 col-12">
          <b-form-group class="mb-0">
            <label class="control-label">Role</label>
            <b-form-select
              :value="participation.role"
              @change="updateParticipation(index, 'role', Number($event))"
            >
              <b-form-select-option :value="0">Follow</b-form-select-option>
              <b-form-select-option :value="1">Lead</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </div>

        <!-- Computed Limit (read-only) -->
        <div class="col-lg-1 col-md-4 col-12">
          <b-form-group class="mb-0">
            <label class="control-label">Limit</label>
            <p class="form-control-plaintext mb-0 text-muted">
              {{ formatCurrency(computeLimit(participation.participationPercent)) }}
            </p>
          </b-form-group>
        </div>

        <!-- Delete -->
        <div class="col-lg-1 col-md-2 col-12 d-flex align-items-end justify-content-end">
          <b-btn variant="link" class="text-danger p-0 mt-3" @click="removeParticipation(index)">
            <i class="fas fa-trash"></i>
          </b-btn>
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mt-2">
      <b-btn variant="outline-success" size="sm" @click="addParticipation">
        <i class="fas fa-plus mr-1"></i>Add Participation
      </b-btn>
      <span class="small" :class="totalClass">
        Total: {{ totalPercent }}%
        <span v-if="totalPercent === 100"> &#10003;</span>
        <span v-if="totalPercent !== 100"> &#9888; Must equal 100%</span>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LayerParticipationEditor',
  props: {
    participations: {
      type: Array,
      default: function () { return [] }
    },
    policies: {
      type: Array,
      default: function () { return [] }
    },
    layerLimit: {
      type: Number,
      default: 0
    }
  },
  computed: {
    totalPercent: function () {
      var total = 0
      var parts = this.participations || []
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i]
        var val = parseFloat(p.participationPercent) || 0
        total += val
      }
      return Math.round(total * 10000) / 100
    },
    totalClass: function () {
      if (this.totalPercent === 100) return 'text-success font-weight-bold'
      if (this.totalPercent > 100) return 'text-danger font-weight-bold'
      return 'text-warning font-weight-bold'
    }
  },
  methods: {
    toPercent: function (val) {
      var n = parseFloat(val) || 0
      return Math.round(n * 10000) / 100
    },
    onPercentInput: function (index, rawValue) {
      var pct = parseFloat(rawValue) || 0
      var stored = Math.round(pct * 100) / 10000
      this.updateParticipation(index, 'participationPercent', stored)
    },
    computeLimit: function (participationPercent) {
      var pct = parseFloat(participationPercent) || 0
      var limit = parseFloat(this.layerLimit) || 0
      return Math.round(pct * limit * 100) / 100
    },
    formatCurrency: function (val) {
      if (val === null || val === undefined || val === '') return '$0'
      return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    getCarrierName: function (policyId) {
      if (!policyId) return ''
      var found = null
      var ps = this.policies || []
      for (var i = 0; i < ps.length; i++) {
        if (ps[i].id === policyId) { found = ps[i]; break }
      }
      return (found && found.carrierName) ? found.carrierName : ''
    },
    addParticipation: function () {
      var updated = (this.participations || []).slice()
      updated.push({
        policyId: null,
        carrierName: '',
        participationPercent: 0,
        participationLimit: 0,
        role: 0
      })
      this.$emit('input', updated)
    },
    removeParticipation: function (index) {
      var updated = (this.participations || []).slice()
      updated.splice(index, 1)
      this.$emit('input', updated)
    },
    updateParticipation: function (index, field, value) {
      var updated = (this.participations || []).slice()
      var item = Object.assign({}, updated[index])
      item[field] = value
      // When policyId changes, auto-populate carrierName from the policies list
      if (field === 'policyId') {
        item.carrierName = this.getCarrierName(value)
      }
      // Recompute participationLimit when percent changes
      if (field === 'participationPercent') {
        item.participationLimit = this.computeLimit(value)
      }
      updated.splice(index, 1, item)
      this.$emit('input', updated)
    }
  }
}
</script>
